import { useState } from "react";
import { Trash2, Loader2, TriangleAlert } from "lucide-react";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";

// A destructive "Delete all" (or custom-label) button with a built-in confirm
// dialog, async handling, and inline error. Shared by the Extension and Exe
// runner tabs so every "delete all / clear all" control looks and behaves the
// same. Drop it into any toolbar:
//
//   <DeleteAllButton
//     title="Delete all imported cases?"
//     message="Permanently removes every imported case. This can't be undone."
//     onDelete={deleteAllExtCases}
//     onDone={reloadCases}
//   />
export default function DeleteAllButton({
  label = "Delete all",
  title = "Delete everything?",
  message,
  confirmLabel = "Delete all",
  onDelete,
  onDone,
  disabled = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const close = () => {
    if (busy) return; // don't let a mid-delete click dismiss the dialog
    setOpen(false);
    setErr(null);
  };

  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await onDelete();
      setOpen(false);
      if (onDone) onDone(r);
    } catch (e) {
      setErr((e && e.message) || "Delete failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setErr(null);
          setOpen(true);
        }}
        disabled={disabled}
        className={cn(
          "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-xl border border-rose-300 bg-transparent px-3 text-sm font-semibold text-rose-600 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-500/30 dark:text-rose-300 dark:hover:bg-rose-500/10",
          className
        )}
      >
        <Trash2 size={14} /> {label}
      </button>

      <Modal
        open={open}
        onClose={close}
        title={title}
        size="md"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <button
              type="button"
              onClick={confirm}
              disabled={busy}
              className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-rose-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-rose-500 disabled:opacity-60"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
              {confirmLabel}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">{message}</p>
        <p className="mt-2 text-xs font-medium text-rose-500">This action can’t be undone.</p>
        {err && (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50/70 p-3 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
            <TriangleAlert size={15} className="mt-0.5 shrink-0" /> {err}
          </div>
        )}
      </Modal>
    </>
  );
}
